import { Injectable } from '@angular/core';
import {
  HttpRequest, HttpHandler, HttpEvent, HttpInterceptor, HttpErrorResponse
} from '@angular/common/http';
import { Observable, throwError, timer } from 'rxjs';
import { retry } from 'rxjs/operators';

/**
 * RETRY INTERCEPTOR
 * Smart retry with exponential backoff.
 *
 * Policy:
 *  - ONLY retries idempotent methods (GET / HEAD / OPTIONS) by default
 *  - ONLY retries network failures (status 0), 5xx, and 408/504 timeouts
 *  - NEVER retries 4xx (business errors) or 401/403 (auth)
 *  - Opt-in for POST/PUT via header `X-Retry: true` (use ONLY for safe/idempotent operations)
 *  - Max 2 retries, backoff: 1500ms → 4500ms (BASE_DELAY_MS × 3^retryIdx; rxjs retryIdx is 1-based)
 */
@Injectable()
export class RetryInterceptor implements HttpInterceptor {
  private readonly MAX_RETRIES = 2;
  private readonly BASE_DELAY_MS = 500;

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const allowMutationRetry = req.headers.has('X-Retry');
    const stripped = allowMutationRetry
      ? req.clone({ headers: req.headers.delete('X-Retry') })
      : req;

    return next.handle(stripped).pipe(
      retry({
        count: this.MAX_RETRIES,
        delay: (error: any, retryIdx: number) => {
          if (!(error instanceof HttpErrorResponse)) {
            // Timeout errors from timeoutInterceptor are not HttpErrorResponse —
            // retry GET only for these
            if (!this.isIdempotent(stripped) && !allowMutationRetry) {
              return throwError(() => error);
            }
            return timer(this.BASE_DELAY_MS * Math.pow(3, retryIdx));
          }

          const retriable =
            error.status === 0 ||       // Network failure
            error.status >= 500 ||      // 5xx server error
            error.status === 408 ||     // Request timeout
            error.status === 504;       // Gateway timeout

          if (!retriable) return throwError(() => error);

          // Idempotent-only check
          if (!this.isIdempotent(stripped) && !allowMutationRetry) {
            return throwError(() => error);
          }

          // Exponential backoff: 1500ms then 4500ms (BASE_DELAY_MS × 3^retryIdx; retryIdx is 1-based).
          return timer(this.BASE_DELAY_MS * Math.pow(3, retryIdx));
        }
      })
    );
  }

  private isIdempotent(req: HttpRequest<any>): boolean {
    return ['GET', 'HEAD', 'OPTIONS'].includes(req.method.toUpperCase());
  }
}
